import React, { useEffect } from 'react';
import { notification, ConfigProvider } from 'antd';

import WrapperPage from '@Pages/WrapperPage/WrapperPage';

import {
  useGetDictionaryCompaniesQuery,
  useGetDictionaryCountriesQuery,
  useGetDictionaryRequestTypeQuery,
  useGetDictionarySIMCapValueQuery,
} from '@Store/api/dictionaryApiSlice';
import {
  notificationsWebSocket,
  deactivateClient,
} from '@Store/api/notificationsApiSlice';
import { dictionaryActions } from '@Store/slices/dictionarySlice';
import { NotificationContext } from '@Context/NotificationContext';
import { PermissionsContext } from '@Context/PermissionsContext';

import Chart from 'chart.js/auto';
import { CategoryScale } from 'chart.js';

import { currentToken } from '@Store/selectors/authSelector';
import { antTheme } from './antTheme';
import {
  useAppDispatch,
  useAppSelector,
  useAuthRenewal,
  useNotification,
  useUserPermissions,
} from './hooks';
import './global.css';

Chart.register(CategoryScale);

const App: React.FC = () => {
  useAuthRenewal();
  const [api, contextHolder] = notification.useNotification();
  const notificationFunctions = useNotification(api);
  const permissions = useUserPermissions();
  const dispatch = useAppDispatch();

  const requestTypeDict = useGetDictionaryRequestTypeQuery();
  const companiesDict = useGetDictionaryCompaniesQuery();
  const countriesDict = useGetDictionaryCountriesQuery();
  const simCapValueDict = useGetDictionarySIMCapValueQuery();

  const token = useAppSelector(currentToken);

  useEffect(() => {
    if (requestTypeDict.data) {
      dispatch(dictionaryActions.setRequestTypeDict(requestTypeDict.data));
    }
  }, [requestTypeDict?.data]);

  useEffect(() => {
    if (companiesDict?.data) {
      dispatch(dictionaryActions.setCompaniesDict(companiesDict.data));
    }
  }, [companiesDict.data]);

  useEffect(() => {
    if (countriesDict?.data) {
      dispatch(dictionaryActions.setCountriesDict(countriesDict.data));
    }
  }, [countriesDict.data]);

  useEffect(() => {
    if (simCapValueDict?.data) {
      dispatch(dictionaryActions.setSimCapValueDict(simCapValueDict.data));
    }
  }, [simCapValueDict?.data]);

  useEffect(() => {
    dispatch(notificationsWebSocket(token));

    return () => {
      dispatch(deactivateClient());
    };
  }, [token]);

  return (
    <ConfigProvider theme={antTheme}>
      <NotificationContext.Provider value={notificationFunctions}>
        <PermissionsContext.Provider value={permissions}>
          {contextHolder}
          <WrapperPage />
        </PermissionsContext.Provider>
      </NotificationContext.Provider>
    </ConfigProvider>
  );
};

export default App;
